import type { CSSInterpolation, CSSObject } from '@ant-design/cssinjs';
import { useStyleRegister } from '@ant-design/cssinjs';
import { merge } from '@formily/shared';
import type { IAny } from '@yimoka/shared';
import type { ComponentTokenMap, GlobalToken } from 'antd/es/theme/interface';
import React from 'react';

import { useConfig } from '../hooks/use-config';
import { useToken } from '../hooks/use-token';

export type OverrideComponent = keyof ComponentTokenMap | (string & {})

export interface StyleInfo {
  hashId: string
  prefixCls: string
  rootPrefixCls: string
  iconPrefixCls: string
}

export type TokenWithCommonCls<T> = T & {
  /** Wrap component class with `.` prefix */
  componentCls: string
  /** Origin prefix which do not have `.` prefix */
  prefixCls: string
  /** Wrap icon class with `.` prefix */
  iconCls: string
  /** Wrap ant prefixCls class with `.` prefix */
  antCls: string
}

export const genCommonStyle = (
  token: IAny,
  componentPrefixCls: string,
): CSSObject => {
  const { fontFamily, fontSize } = token;

  const rootPrefixSelector = `[class^="${componentPrefixCls}"], [class*=" ${componentPrefixCls}"]`;

  return {
    [rootPrefixSelector]: {
      fontFamily,
      fontSize,
      boxSizing: 'border-box',

      '&::before, &::after': {
        boxSizing: 'border-box',
      },

      [rootPrefixSelector]: {
        boxSizing: 'border-box',

        '&::before, &::after': {
          boxSizing: 'border-box',
        },
      },
    },
  };
};

export type UseComponentStyleResult = [
  (node: React.ReactElement) => React.JSX.Element,  // 修改参数和返回类型
  string
]

export const genStyleHook = <ComponentName extends OverrideComponent>(
  component: ComponentName,
  styleFn: (
    token: TokenWithCommonCls<GlobalToken>,
    info: StyleInfo
  ) => CSSInterpolation,
) => (prefixCls: string): UseComponentStyleResult => {
    const { theme, token, hashId } = useToken();
    const { getPrefixCls, iconPrefixCls, csp } = useConfig();
    const rootPrefixCls = getPrefixCls();
    useStyleRegister(
      {
        nonce: csp?.nonce,
        theme: theme as IAny,
        token,
        hashId,
        path: ['formily-antd', component, prefixCls, iconPrefixCls],
      },
      () => {
        const componentCls = `.${prefixCls}`;
        const mergedToken: TokenWithCommonCls<GlobalToken> = merge(token, {
          ...token.Form, // Merge the antd form token
          componentCls,
          prefixCls,
          iconCls: `.${iconPrefixCls}`,
          antCls: `.${rootPrefixCls}`,
        });

        const styleInterpolation = styleFn(mergedToken, {
          hashId,
          prefixCls,
          rootPrefixCls,
          iconPrefixCls,
        });
        return [genCommonStyle(token, prefixCls), styleInterpolation];
      },
    );
    return [
      (node: React.ReactElement) => node as React.JSX.Element,
      hashId,
    ];
  };
